import { mapValues } from 'lodash-es';
import * as Yup from 'yup';
import type { ComponentType } from 'react';
import type { FallbackNs } from 'react-i18next';

import { useTranslation, type TFunction, type Namespace } from '@yoweb/next-i18next';
import { useIntlMeta, type UseIntlMetaValue } from '@yoweb/ui/hooks/useIntlMeta';

export type YoFormObject<T> = {
  /** Yup validation schema */
  validation: Yup.AnySchema;

  /** The default value for the initial formik state, defaults to the empty string. */
  defaultValue?: T;

  /** Component to render the form value, defaults to the Input components. */
  component?: string | ComponentType<any>;

  /** props to go along with the supplied component. */
  props?: Record<string, any>;
};

export type YoForm = Record<
  string,
  // TODO(rthor): Add a generic type.
  //  - I don't how circumvent the generic default, so that it correctly in first the type.
  //  - Object methods do not have a good generics support.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  YoFormObject
>;

export type YoFormFactory<Ns extends Namespace> = (
  t: TFunction<FallbackNs<Ns>>,
  meta: UseIntlMetaValue,
) => YoForm;

/**
 * Your form factory function.
 *
 * @example
 *  const myUniqueForm = createYoForm({
 *    firstName: {
 *      defaultValue: 'John',
 *      validation: Yup.string().required(),
 *    }),
 *    emailAddress: {
 *      defaultValue: 'matt+testing+infra@yohana.com',
 *      validation: Yup.string().email().required(),
 *    },
 *  })
 */
function createYoForm<Form extends YoForm>(form: Form) {
  const schema = Yup.object(mapValues(form, ({ validation }) => validation)).required();
  const initialState = mapValues(form, ({ defaultValue }) => defaultValue ?? '');
  const render = mapValues(form, ({ component, props }, name) => ({
    Component: component ?? null,
    props: props ?? { label: name },
  }));

  return { initialState, render, schema };
}

export function useYoForm<T extends Namespace>(ns: T, form: YoFormFactory<T>) {
  const { t } = useTranslation(ns);
  const meta = useIntlMeta();

  return createYoForm(form(t, meta));
}
